import React, { useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { ErrorMessage, LoadingState } from '@sketch/components'
import {
  AnnotationQueryVariablesProvider,
  RouteParams,
  useUserSignedIn,
} from '@sketch/modules-common'

import {
  CanvasInteractionsProvider,
  CanvasSelectionProvider,
} from '@sketch/canvas'
import {
  CanvasLeftPanel,
  CanvasLeftPanelResizingProvider,
} from '../../../canvas/components/CanvasLeftPanel'
import { CanvasScopeProvider } from '../../../canvas/scope'
import { CanvasSurface } from '../../components/CanvasSurface'
import {
  CanvasMobileToolbar,
  PAGE_CANVAS_TOOLBAR_ITEMS,
} from '../../components/CanvasMobileToolbar'
import { FrameInspectorProvider } from '../../FrameView/components/FrameInspectorProvider'
import { FrameSelectorForPreview } from '../../FrameView/components/FrameSelector/FrameSelectorForPreview'
import { SectionSelector } from '../../SectionView/components/SectionSelector'
import { FloatingZoom } from '../../FrameView/components/FloatingZoom'
import { Panel } from '../../components/Panel'
import { DocumentSidebarLayoutExtraProps } from '../../components/DocumentSidebarLayout'
import { GridsAndLayoutsProvider } from '../../components/GridsAndLayoutsProvider'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64'

import PageCanvasViewHeader from '../../PageCanvasView/components/PageCanvasViewHeader'
import { PageSelectorForPreview } from '../../PageCanvasView/components/PageSelector'
import { ErrorWrapper } from '../../PageCanvasView/PageCanvasView.styles'
import { LoadingMessage } from '../../components/LoadingMessage'

import { useVersioning } from '../../../versioning'
import { usePreview } from './PreviewContext'
import { usePreviewSingleEntity } from './usePreviewSingleEntity'
import { CanvasAnnotationOverlayContext } from '../../../canvas/components/CanvasAnnotationOverlayContext'
import type { GetPreviewDetailsQuery } from '@sketch/gql-types'
import { noop } from '@sketch/utils'

type PageCanvasViewProps = {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
} & DocumentSidebarLayoutExtraProps

type PageCanvasContentProps = {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  permanentPageId: string
} & DocumentSidebarLayoutExtraProps

/**
 * The Preview's canvas. The renderer is mounted above this, in PreviewView, so
 * that it survives page↔entity navigation and the move between the canvas and
 * the prototype view - the renderer re-roots and animates rather than being
 * torn down and re-initialised.
 */
function PageCanvasContent({
  preview,
  permanentPageId,
  HeaderPortal,
  SidebarRightPortal,
  toggleSidebarRight,
  isSidebarRightOpen,
}: PageCanvasContentProps) {
  const focus = usePreviewSingleEntity()
  const isShowingSingleFrame = focus.kind === 'frame'
  const isShowingSingleSection = focus.kind === 'section'
  const { pageUUID } = useParams<Partial<RouteParams<'PREVIEW_FRAME_VIEW'>>>()

  const previewContext = usePreview()
  const { currentVersion } = useVersioning()
  const isSignedIn = useUserSignedIn()

  const canvasRef = useRef<HTMLDivElement>(null)

  // In the frame and Section focus views the pageUUID comes from the URL;
  // otherwise fall back to the page from context (or the first page).
  const isShowingSingleEntity = focus.kind !== 'page'
  const activePageId =
    (isShowingSingleEntity ? pageUUID : null) ?? permanentPageId

  const subjects = useMemo(
    () => [{ type: 'PAGE' as const, permanentId: activePageId }],
    [activePageId]
  )

  const disableAnnotations = !preview.commentsEnabled || !isSignedIn
  const userCanSeeComments =
    (previewContext && preview.commentsEnabled) || !previewContext
  const presentationStatus = preview.presentationStatus ?? 'PENDING'
  const isProcessingPresentationFile = presentationStatus === 'PENDING'

  const leftPanelNavigation = isShowingSingleSection ? (
    <SectionSelector />
  ) : isShowingSingleFrame ? (
    <FrameSelectorForPreview />
  ) : (
    <PageSelectorForPreview previewIdentifier={preview.identifier} />
  )

  return (
    <CanvasScopeProvider pageUUID={activePageId}>
      {/*
        In a focus view: scope the inspector layer tree to the focused entity's
        children by passing rootElementUUID. autoSelectRootInitially then selects
        that element automatically (it's now the root of the inspector tree).
      */}
      <FrameInspectorProvider>
        <AnnotationQueryVariablesProvider subjects={subjects} isPreview>
          <CanvasAnnotationOverlayContext
            disabled={disableAnnotations}
            isViewingLatestVersion={true}
          >
            <HeaderPortal>
              <PageCanvasViewHeader
                setSidebarLeftOpen={noop}
                toggleSidebarRight={toggleSidebarRight}
                isSidebarRightOpen={isSidebarRightOpen}
                isViewingLatestVersion={true}
                isViewingStarredVersion={false}
                hasPendingPatches={false}
                userCanOpenInApp={false}
                currentVersion={currentVersion}
              />
              <Panel
                userCanSeeComments={userCanSeeComments}
                userCanInspect={preview.inspectEnabled}
                SidebarRightPortal={SidebarRightPortal}
                version={currentVersion}
              />
            </HeaderPortal>

            <FloatingZoom />

            {isProcessingPresentationFile ? (
              <LoadingState
                data-testid="loader-preview-canvas"
                type="color-icon"
                size="48px"
                message={<LoadingMessage />}
              />
            ) : (
              <>
                {activePageId ? (
                  <CanvasSurface canvasRef={canvasRef}>
                    <CanvasLeftPanel>{leftPanelNavigation}</CanvasLeftPanel>
                  </CanvasSurface>
                ) : (
                  <ErrorWrapper>
                    <ErrorMessage
                      title="No pages in this preview"
                      icon={<DocumentIcon />}
                      iconSize="medium"
                    >
                      No pages have been added here yet.
                    </ErrorMessage>
                  </ErrorWrapper>
                )}
              </>
            )}
            <CanvasMobileToolbar items={PAGE_CANVAS_TOOLBAR_ITEMS} />
          </CanvasAnnotationOverlayContext>
        </AnnotationQueryVariablesProvider>
      </FrameInspectorProvider>
    </CanvasScopeProvider>
  )
}

export function PageCanvasView(props: PageCanvasViewProps) {
  const { preview, ...layoutProps } = props
  const previewContext = usePreview()

  const firstPageUUID = preview.pages.entries[0]?.uuid
  const permanentPageId =
    previewContext?.currentPage?.uuid || firstPageUUID || ''

  return (
    <CanvasInteractionsProvider>
      <CanvasSelectionProvider>
        <CanvasLeftPanelResizingProvider>
          <GridsAndLayoutsProvider>
            <PageCanvasContent
              preview={preview}
              permanentPageId={permanentPageId}
              {...layoutProps}
            />
          </GridsAndLayoutsProvider>
        </CanvasLeftPanelResizingProvider>
      </CanvasSelectionProvider>
    </CanvasInteractionsProvider>
  )
}
